import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useClient } from 'urql';

import { FeatureFlag } from '@revelio/core';

import { useKibanaLogger } from '../../hooks/kibana-logger/kibana-logger';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import {
  fetchExpandedSelectionListsFromAPI,
  fetchSelectionListsFromAPI,
} from '../api/fetchSelectionLists';
import type { GqlSelectionListKey, SelectionListState } from '../types';
import {
  getFromMemoryCache,
  getSelectionListsFromStore,
  setSelectionListStore,
  updateMemoryCache,
} from '../utils/cache';
import { sanitizeSelectionLists } from '../utils/validation';

/**
 * Shared hook for fetching non-role selection lists
 * Used by both Custom Dash and Data Builder
 */
export const useSelectionLists = <T extends GqlSelectionListKey>(
  selectionListIds: T[]
): SelectionListState<T> | null => {
  const gqlClient = useClient();
  const { kibanaLogger } = useKibanaLogger();
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);
  const [selectionListState, setSelectionListState] =
    useState<SelectionListState<T> | null>(() =>
      getFromMemoryCache(selectionListIds, {
        referenceListsMerged: useReferenceApi,
      })
    );

  const updateSelectList = useCallback((newValue: SelectionListState<T>) => {
    setSelectionListState((currentValue) => {
      if (isEqual(newValue, currentValue)) {
        return currentValue;
      }

      return newValue;
    });
  }, []);

  useEffect(() => {
    if (selectionListIds.length === 0) return;

    (async () => {
      try {
        // first check local cache
        const cachedLists = getFromMemoryCache(selectionListIds, {
          referenceListsMerged: useReferenceApi,
        });
        if (cachedLists) {
          updateSelectList(cachedLists);
          return;
        }

        // check if store has the selection list
        const selectionListsStore = await getSelectionListsFromStore(
          selectionListIds,
          { referenceListsMerged: useReferenceApi }
        );
        if (selectionListsStore) {
          const pickedSelectionLists = selectionListIds.reduce(
            (acc, curr) => {
              acc[curr] = selectionListsStore[curr] || [];
              return acc;
            },
            {} as SelectionListState<T>
          );
          updateSelectList(pickedSelectionLists);
          updateMemoryCache(selectionListsStore, {
            referenceListsMerged: useReferenceApi,
          });
          return;
        }

        // if not found in local cache or store, fetch the selection lists
        // biome-ignore lint/suspicious/noImplicitAnyLet: migrated from develop
        let selectionListData;
        const allWithinDashboardSelectionLists = selectionListIds.every((id) =>
          DASHBOARD_SELECTON_LISTS.includes(id)
        );
        if (allWithinDashboardSelectionLists) {
          const { data } = await fetchSelectionListsFromAPI(
            gqlClient,
            useReferenceApi
          );
          selectionListData = data;
        } else {
          selectionListData = await fetchExpandedSelectionListsFromAPI(
            gqlClient,
            useReferenceApi
          );
        }

        const { data: sanitized, strippedKeys } =
          sanitizeSelectionLists(selectionListData);

        if (strippedKeys.length > 0) {
          kibanaLogger?.warn(
            `SelectionLists API returned null fields: ${strippedKeys.join(', ')}`,
            { context: 'selection-lists-partial-data' }
          );
        }

        if (!sanitized) {
          return;
        }

        const pickedSelectionLists = selectionListIds.reduce(
          (acc, curr) => {
            if (!sanitized[curr]) return acc;
            acc[curr] = sanitized[curr] ?? [];
            return acc;
          },
          {} as SelectionListState<T>
        );

        updateSelectList(pickedSelectionLists);
        updateMemoryCache(sanitized, {
          referenceListsMerged: useReferenceApi,
        });
        await setSelectionListStore(sanitized, {
          referenceListsMerged: useReferenceApi,
        });
      } catch (error) {
        console.error('Error fetching selection lists', error);
      }
    })();
  }, [
    selectionListIds,
    gqlClient,
    updateSelectList,
    kibanaLogger,
    useReferenceApi,
  ]);

  return selectionListState;
};

// this is so we can know when to fetch "expanded" selection lists (e.g. for data builder) that needs more selection lists
const DASHBOARD_SELECTON_LISTS: GqlSelectionListKey[] = [
  'city',
  'company',
  'country',
  'ethnicity',
  'gender',
  'highest_degree',
  'msa',
  'region',
  'rics_k10',
  'rics_k400',
  'rics_k50',
  'seniority',
  'state',
  'skill_k3000',
  'skill_k700',
  'skill_k75',
];
